import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Kanban, FileText, CircleNotch, ArrowLeft, Target, ArrowClockwise, WarningOctagon, FloppyDisk, Books, Check, PencilSimple, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import KanbanBoard from "@/components/KanbanBoard";
import CardModal from "@/components/CardModal";
import ChatSidebar from "@/components/ChatSidebar";
import Exporter from "@/components/Exporter";
import { api } from "@/lib/api";
import { normalizeSteps } from "@/lib/planiq";
import ThemeToggle from "@/components/ThemeToggle";
import Logomark from "@/components/Logomark";
import UserMenu from "@/components/UserMenu";
import { useAuth } from "@/lib/auth";

export default function PlanView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFull = !!user && (user.role === "admin" || user.tier === "full");
  const lockReason = !user ? "login" : (!isFull ? "tier" : null);
  const [plan, setPlan] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard"); // dashboard | document
  const [activeCard, setActiveCard] = useState(null);
  const [scope, setScope] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const startEditTitle = () => {
    setTitleDraft(plan.title);
    setEditingTitle(true);
  };

  const saveTitle = async () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!next || next === plan.title) return;
    const prev = plan.title;
    setPlan((p) => ({ ...p, title: next }));
    try {
      await api.renamePlan(id, next);
      toast.success("Plan name updated.");
    } catch (e) {
      setPlan((p) => ({ ...p, title: prev }));
      toast.error("Could not rename the plan.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.savePlan(id);
      setPlan((p) => ({ ...p, saved: true }));
      toast.success("Saved to your library.");
    } catch (e) {
      toast.error("Could not save the plan.");
    } finally {
      setSaving(false);
    }
  };

  const retry = async () => {
    setRetrying(true);
    try {
      await api.generate(id, plan.pillars, plan.blueprint?.inferred_constraints);
      setPlan((p) => ({ ...p, status: "generating" }));
    } catch (e) {
      toast.error("Retry failed. Please try again.");
    } finally {
      setRetrying(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([api.getPlan(id), api.getCards(id)]);
      setPlan(p);
      setCards(c);
      return p;
    } catch (e) {
      toast.error("Plan not found.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Whenever the user opens the Document/export view, refetch the plan + cards from the
  // server (the single source of truth the PDF/DOCX are built from) so the exportable
  // document can never drift behind board edits or AI refinements.
  useEffect(() => {
    if (view === "document" && plan?.status === "ready") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // route pre-generation plans back to the blueprint gate
  useEffect(() => {
    if (plan && (plan.status === "analyzing" || plan.status === "blueprint_ready")) {
      navigate(`/plan/${id}/blueprint`, { replace: true });
    }
  }, [plan, id, navigate]);

  // poll while plan is generating
  useEffect(() => {
    if (plan?.status !== "generating") return;
    const t = setInterval(async () => {
      const p = await api.getPlan(id).catch(() => null);
      if (!p) return;
      if (p.status === "ready") {
        const c = await api.getCards(id);
        setPlan(p);
        setCards(c);
      } else if (p.status === "error") {
        clearInterval(t);
        setPlan(p);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [plan?.status, id]);

  const pillarById = plan ? Object.fromEntries(plan.pillars.map((p) => [p.id, p])) : {};

  const reorderCards = async (horizon, orderedIds) => {
    setCards((prev) => prev.map((c) => {
      if (c.horizon !== horizon) return c;
      const idx = orderedIds.indexOf(c._id);
      return idx === -1 ? c : { ...c, order: idx };
    }));
    try {
      await api.reorderCards(id, horizon, orderedIds);
    } catch (e) {
      toast.error("Could not reorder cards.");
      load();
    }
  };

  const toggleStep = async (cardId, index) => {
    const card = cards.find((c) => c._id === cardId);
    if (!card) return;
    const steps = normalizeSteps(card.steps).map((s, i) => (i === index ? { ...s, done: !s.done } : s));
    setCards((prev) => prev.map((c) => (c._id === cardId ? { ...c, steps } : c)));
    setActiveCard((c) => (c && c._id === cardId ? { ...c, steps } : c));
    try {
      await api.patchCard(cardId, { steps });
    } catch (e) {
      toast.error("Could not update sub-task.");
      load();
    }
  };

  // Apply a fresh card set from an AI refinement, keeping the open modal's card in sync.
  const applyCards = useCallback((newCards) => {
    setCards(newCards);
    setActiveCard((c) => (c ? newCards.find((n) => n._id === c._id) || null : null));
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500 font-mono">
          <CircleNotch size={22} className="animate-spin" /> Loading plan…
        </div>
      </div>
    );
  }

  if (!plan) return null;

  if (plan.status === "generating") {
    return (
      <div className="h-screen flex flex-col items-center justify-center text-center px-6" data-testid="generating-state">
        <CircleNotch size={36} className="animate-spin text-gray-900 mb-5" />
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-gray-500 mb-2">Synthesizing</p>
        <h1 className="font-display font-black text-3xl tracking-tight mb-3">Building your plan</h1>
        <p className="text-gray-500 max-w-md font-mono text-sm">
          Go grab a coffee while we prepare your plan — Xartis is weighing your context against every focus area and phase. Crafting something genuinely useful takes a few minutes.
        </p>
      </div>
    );
  }

  if (plan.status === "error") {
    return (
      <div className="h-screen flex flex-col items-center justify-center text-center px-6" data-testid="error-state">
        <WarningOctagon size={40} weight="duotone" className="text-red-600 mb-4" />
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-gray-500 mb-2">Generation hiccup</p>
        <h1 className="font-display font-black text-3xl tracking-tight mb-3">We couldn't finish this build</h1>
        <p className="text-gray-500 max-w-md text-sm mb-7">
          The AI returned a malformed plan. Your context and pillars are saved — just run it again.
        </p>
        <div className="flex gap-3">
          <button onClick={retry} disabled={retrying} data-testid="retry-generate-button"
            className="inline-flex items-center gap-2 rounded-none bg-gray-950 hover:bg-gray-700 text-white px-6 py-3 text-sm font-semibold disabled:opacity-50">
            {retrying ? <CircleNotch size={18} className="animate-spin" /> : <ArrowClockwise size={18} weight="bold" />}
            Try Again
          </button>
          <button onClick={() => navigate("/start")} data-testid="start-over-button"
            className="inline-flex items-center gap-2 rounded-none border border-gray-900 px-6 py-3 text-sm font-semibold hover:bg-gray-50">
            Start Over
          </button>
        </div>
      </div>
    );
  }

  const allSteps = cards.flatMap((c) => normalizeSteps(c.steps));
  const totalSteps = allSteps.length;
  const doneSteps = allSteps.filter((s) => s.done).length;
  const pct = totalSteps ? Math.round((doneSteps / totalSteps) * 100) : 0;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* top nav */}
      <header className="no-print border-b border-gray-200 px-6 py-3 flex items-center justify-between bg-white">
        <div className="flex items-center gap-4 min-w-0">
          <button onClick={() => navigate("/library")} data-testid="home-button"
            className="text-gray-500 hover:text-gray-900 shrink-0">
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2">
                <Logomark size={20} />
                <span className="font-display font-black text-lg tracking-tight">Xartis</span>
              </span>
              <span className="text-gray-300">/</span>
              {editingTitle ? (
                <input
                  data-testid="plan-title-input"
                  autoFocus
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); saveTitle(); }
                    if (e.key === "Escape") { setEditingTitle(false); }
                  }}
                  className="text-sm font-semibold text-gray-800 bg-transparent border-b border-gray-900 focus:outline-none min-w-[160px] max-w-[380px]"
                />
              ) : (
                <button onClick={startEditTitle} data-testid="plan-title-edit" title="Click to rename"
                  className="group flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 truncate transition-colors">
                  <span className="truncate">{plan.title}</span>
                  <PencilSimple size={13} className="shrink-0 text-gray-400 group-hover:text-gray-700 transition-colors" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2.5 mt-0.5" data-testid="subtask-progress">
              <p className="text-[11px] font-mono text-gray-400">
                {doneSteps}/{totalSteps} sub-tasks · {pct}% · {plan.pillars.length} pillars
              </p>
              <div className="h-1 w-20 bg-gray-100 shrink-0" data-testid="subtask-progress-bar">
                <div className="h-full bg-gray-950 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* save / library */}
          <button onClick={save} disabled={saving || plan.saved} data-testid="save-plan-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-gray-900 hover:bg-gray-50 transition-colors disabled:opacity-60">
            {plan.saved ? <><Check size={16} weight="bold" /> Saved</> : saving ? <><CircleNotch size={16} className="animate-spin" /> Saving</> : <><FloppyDisk size={16} weight="duotone" /> Save</>}
          </button>
          <button onClick={() => navigate("/library")} data-testid="library-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-gray-300 hover:border-gray-900 transition-colors">
            <Books size={16} weight="duotone" /> Library
          </button>

          {/* view toggle */}
          <div className="inline-flex border border-gray-300" data-testid="view-toggle">
            <button onClick={() => setView("dashboard")} data-testid="view-dashboard"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold ${view === "dashboard" ? "bg-gray-950 text-white" : "bg-white text-gray-600"}`}>
              <Kanban size={16} /> Dashboard
            </button>
            <button onClick={() => setView("document")} data-testid="view-document"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border-l border-gray-300 ${view === "document" ? "bg-gray-950 text-white" : "bg-white text-gray-600"}`}>
              <FileText size={16} /> Document
            </button>
          </div>

          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      {plan.is_sample && (
        <div className="no-print bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center gap-3" data-testid="sample-banner">
          <Sparkle size={16} weight="fill" className="text-amber-500 shrink-0" />
          <p className="text-sm text-amber-900">
            <span className="font-semibold">You're exploring a sample plan.</span> Loved it? Build your own from your real context — it takes a couple of minutes.
          </p>
          <button onClick={() => navigate("/start")} data-testid="sample-banner-cta"
            className="ml-auto inline-flex items-center gap-1.5 bg-gray-950 hover:bg-gray-700 text-white text-sm font-semibold px-4 py-1.5 transition-colors shrink-0">
            Start your own plan <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {view === "dashboard" ? (
          <>
            <main className="flex-1 overflow-y-auto p-6 animate-reveal">
              {cards.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <Target size={40} className="text-gray-300 mb-3" weight="duotone" />
                  <p className="text-gray-500 font-mono text-sm">No action items generated.</p>
                </div>
              ) : (
                <KanbanBoard cards={cards} pillarById={pillarById}
                  onCardClick={setActiveCard} onReorder={reorderCards} onToggleStep={toggleStep} planType={plan.plan_type} />
              )}
            </main>
            <ChatSidebar planId={id} pillars={plan.pillars} planType={plan.plan_type}
              constraints={plan.blueprint?.inferred_constraints} scope={scope}
              onClearScope={() => setScope(null)} onCardsUpdate={applyCards} lockReason={lockReason} />
          </>
        ) : (
          <Exporter plan={plan} cards={cards} pillars={plan.pillars}
            onSummaryUpdate={(s) => setPlan((p) => ({ ...p, executive_summary: s }))} lockReason={lockReason} />
        )}
      </div>

      <CardModal card={activeCard} pillar={activeCard ? pillarById[activeCard.pillar_id] : null}
        open={!!activeCard} onClose={() => setActiveCard(null)}
        onToggleStep={toggleStep} planId={id} onCardsUpdate={applyCards} lockReason={lockReason} />
    </div>
  );
}
